import React, { createContext, useContext, useState, useEffect } from 'react';
import supabase from '../lib/supabase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registrationStatus, setRegistrationStatus] = useState(null);

  // Hardcoded admin credentials - these bypass Supabase authentication
  const HARDCODED_ADMIN = {
    email: 'admin@gospelwiseauthor.com',
    password: '60163fsnje',
    id: 'admin-1',
    name: 'Admin',
    subscription: 'admin',
    role: 'superadmin'
  };

  useEffect(() => {
    // Check for existing session
    const checkSession = async () => {
      // First check local storage for our custom session
      const savedUser = localStorage.getItem('gospelwise_user');
      if (savedUser) {
        setUser(JSON.parse(savedUser));
        setLoading(false);
        return;
      }

      // If not in local storage, check Supabase session
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          // Get user metadata from auth
          const { data: { user: authUser } } = await supabase.auth.getUser();
          const userMetadata = authUser?.user_metadata || {};

          // Get user profile from Supabase profiles table
          const { data: profile } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', session.user.id)
            .single();

          // Priority for name: 1. Profile name, 2. Auth metadata name, 3. Default
          const displayName = profile?.name || profile?.full_name || userMetadata?.full_name || 'User';

          const userSession = {
            id: session.user.id,
            email: session.user.email,
            name: displayName,
            subscription: profile?.subscription || 'free',
            role: profile?.role || 'user'
          };

          setUser(userSession);
          localStorage.setItem('gospelwise_user', JSON.stringify(userSession));
        }
      } catch (error) {
        console.error("Session check error:", error);
      } finally {
        setLoading(false);
      }
    };

    checkSession();
  }, []);

  const login = async (email, password) => {
    // Check for hardcoded admin first
    if (email.toLowerCase() === HARDCODED_ADMIN.email.toLowerCase()) {
      if (password !== HARDCODED_ADMIN.password) {
        throw new Error('Invalid admin credentials');
      }
      
      // Set admin session
      setUser(HARDCODED_ADMIN);
      localStorage.setItem('gospelwise_user', JSON.stringify(HARDCODED_ADMIN));
      return HARDCODED_ADMIN;
    }

    // For regular users, use Supabase authentication
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;

      // Get user metadata from auth
      const { data: { user: authUser } } = await supabase.auth.getUser();
      const userMetadata = authUser?.user_metadata || {};

      // Get user profile
      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', data.user.id)
        .single();

      // Priority for name: 1. Profile name, 2. Auth metadata name, 3. Default
      const displayName = profile?.name || profile?.full_name || userMetadata?.full_name || 'User';

      const userSession = {
        id: data.user.id,
        email: data.user.email,
        name: displayName,
        subscription: profile?.subscription || 'free',
        role: profile?.role || 'user'
      };

      setUser(userSession);
      localStorage.setItem('gospelwise_user', JSON.stringify(userSession));
      return userSession;
    } catch (error) {
      // Fallback to local storage for existing users (migration period)
      try {
        const registeredUsers = JSON.parse(localStorage.getItem('gospelwise_registered_users') || '[]');
        const existingUser = registeredUsers.find(
          u => u.email.toLowerCase() === email.toLowerCase() && u.password === password
        );

        if (!existingUser) {
          throw new Error('Invalid email or password. Please check your credentials or register for an account.');
        }

        // Create user session without storing password
        const userSession = {
          id: existingUser.id,
          email: existingUser.email,
          name: existingUser.name,
          subscription: existingUser.subscription || 'free',
          role: existingUser.role || 'user'
        };

        setUser(userSession);
        localStorage.setItem('gospelwise_user', JSON.stringify(userSession));
        return userSession;
      } catch (err) {
        throw new Error('Invalid email or password. Please check your credentials or register for an account.');
      }
    }
  };

  const register = async (name, email, password) => {
    // Clear any previous registration status
    setRegistrationStatus(null);

    // Prevent registration with admin email
    if (email.toLowerCase() === HARDCODED_ADMIN.email.toLowerCase()) {
      throw new Error('Cannot register with this email address');
    }

    try {
      console.log('Starting registration process for:', email);
      
      // First, test if we can connect to Supabase
      const { data: testData, error: testError } = await supabase
        .from('profiles')
        .select('count')
        .limit(1);

      if (testError) {
        console.error('Supabase connection test failed:', testError);
        throw new Error('Unable to connect to database. Please check your connection and try again.');
      }

      console.log('Supabase connection test successful');
      
      // Register with Supabase Auth - the trigger will handle profile creation
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: name,
            name: name
          }
        }
      });

      if (error) {
        console.error('Supabase auth signup error:', error);
        
        // Provide more specific error messages
        if (error.message.includes('User already registered')) {
          throw new Error('An account with this email already exists. Please try logging in instead.');
        } else if (error.message.includes('Invalid email')) {
          throw new Error('Please enter a valid email address.');
        } else if (error.message.includes('Password')) {
          throw new Error('Password must be at least 6 characters long.');
        } else {
          throw new Error(`Registration failed: ${error.message}`);
        }
      }

      console.log('Supabase auth signup successful:', data);

      // The database trigger should have created the profile automatically
      // Let's verify it was created and create it manually if needed
      if (data.user) {
        console.log('Verifying profile creation for user:', data.user.id);
        
        // Wait a moment for the trigger to execute
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Check if profile exists
        const { data: existingProfile, error: checkError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', data.user.id)
          .single();

        if (checkError && checkError.code === 'PGRST116') {
          // Profile doesn't exist, create it manually as fallback
          console.log('Profile not found, creating manually...');
          
          const profileData = {
            id: data.user.id,
            name: name,
            full_name: name,
            email: email,
            subscription: 'free',
            role: 'user',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };

          const { data: newProfile, error: profileError } = await supabase
            .from('profiles')
            .insert([profileData])
            .select()
            .single();

          if (profileError) {
            console.error("Error creating profile manually:", profileError);
            // Don't throw here - the user account was created successfully
            console.log('User account created but profile creation failed. Profile will be created on first login.');
          } else {
            console.log('Profile created manually:', newProfile);
          }
        } else if (existingProfile) {
          console.log('Profile found:', existingProfile);
        } else {
          console.error('Error checking profile:', checkError);
        }
      }

      // Set registration status for conditional redirection
      setRegistrationStatus({
        success: true,
        message: "Your account has been created! Please check your email to confirm your registration."
      });

      // Note: We don't set the user session here - we'll wait for email confirmation
      return { success: true, requiresEmailConfirmation: true };

    } catch (error) {
      console.error("Registration error:", error);
      
      // Set error status
      setRegistrationStatus({
        success: false,
        message: error.message || "Registration failed. Please try again."
      });

      // Re-throw the error so the UI can handle it
      throw error;
    }
  };

  const logout = async () => {
    // Clear local session
    setUser(null);
    localStorage.removeItem('gospelwise_user');

    // Also sign out from Supabase
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const isAdmin = () => {
    return user?.role === 'admin' || user?.role === 'superadmin';
  };

  const isSuperAdmin = () => {
    return user?.role === 'superadmin';
  };

  // Get all registered users (admin only)
  const getAllUsers = async () => {
    if (!isAdmin()) {
      throw new Error('Unauthorized: Admin access required');
    }

    try {
      // Try to get users from Supabase
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching users from Supabase:', error);
        throw error;
      }

      if (data && data.length > 0) {
        return data.map(user => ({
          id: user.id,
          name: user.name || user.full_name || 'Unnamed User',
          email: user.email,
          subscription: user.subscription || 'free',
          role: user.role || 'user',
          createdAt: user.created_at
        }));
      }

      // If no data, return empty array
      return [];
    } catch (error) {
      console.error("Error getting users:", error);
      throw error;
    }
  };

  // Update user profile data
  const updateUserProfile = async (userData) => {
    if (!user) return null;

    try {
      // If email is being updated, we need to update it in auth first
      if (userData.email && userData.email !== user.email) {
        const { data, error: updateAuthError } = await supabase.auth.updateUser({
          email: userData.email
        });

        if (updateAuthError) throw updateAuthError;

        // The trigger will automatically update the email in profiles table
        // We just need to update the other fields
        const { error: updateProfileError } = await supabase
          .from('profiles')
          .update({
            ...userData,
            updated_at: new Date().toISOString()
          })
          .eq('id', user.id);

        if (updateProfileError) throw updateProfileError;
      } else {
        // If no email update, just update other profile fields
        const { error } = await supabase
          .from('profiles')
          .update({
            ...userData,
            updated_at: new Date().toISOString()
          })
          .eq('id', user.id);

        if (error) throw error;
      }

      // Update local user state
      const updatedUser = { ...user, ...userData };
      setUser(updatedUser);
      localStorage.setItem('gospelwise_user', JSON.stringify(updatedUser));

      return updatedUser;
    } catch (error) {
      console.error("Error updating user profile:", error);
      throw error;
    }
  };

  const value = {
    user,
    login,
    register,
    logout,
    loading,
    isAdmin,
    isSuperAdmin,
    getAllUsers,
    updateUserProfile,
    registrationStatus,
    setRegistrationStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};